import {
  Box,
  Divider,
  IconButton,
  Paper,
  Portal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, ContentSave, Draw } from "mdi-material-ui";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { useAddClubComment, useClubComments } from "../lib/index.js";

export const ClubComments = ({
  clubId,
  onClose,
}: {
  clubId: number;
  onClose(): void;
}) => {
  const { t } = useTranslation("ClubComment");
  const { data: club } = useClubComments(clubId);
  const { mutate: addComment, isSuccess } = useAddClubComment();

  const handleSubmit = (formEl: HTMLFormElement) => {
    const { comment } = Object.fromEntries(new FormData(formEl)) as {
      comment: string;
    };
    addComment({ clubId, comment });
  };

  return (
    <Portal>
      <Paper
        sx={{
          position: "fixed",
          top: ({ spacing }) => spacing(7),
          right: ({ spacing }) => spacing(4),
          width: 280,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", my: 1, mx: 2 }}>
          <Typography
            variant="h3"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {club?.name}
          </Typography>
          <IconButton sx={{ ml: "auto", mr: -1 }} onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ mx: 2 }}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit(event.currentTarget);
            }}
          >
            <TextField
              key={String(isSuccess)}
              name="comment"
              variant="outlined"
              multiline
              size="small"
              label={t("newComment")}
              fullWidth
              inputProps={{
                onKeyPress: (event) => {
                  if (
                    event.code === "Enter" &&
                    !event.shiftKey &&
                    event.currentTarget.form
                  ) {
                    event.preventDefault();
                    handleSubmit(event.currentTarget.form);
                  }
                },
              }}
            />
            <Box sx={{ display: "flex" }}>
              <Tooltip title={t("managedSignatures")}>
                <IconButton
                  sx={{ ml: -1 }}
                  onClick={() => {
                    addComment({ clubId, comment: t("managedSignatures") });
                  }}
                >
                  <Draw />
                </IconButton>
              </Tooltip>
              <IconButton
                type="submit"
                color="primary"
                sx={{ ml: "auto", mr: -1 }}
              >
                <ContentSave />
              </IconButton>
            </Box>
          </form>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
          {club?.comments.map(({ id, comment, timestamp, member }, index) => (
            <Fragment key={id}>
              {!!index && <Divider />}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.5,
                  px: 2,
                  whiteSpace: "nowrap",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {member.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ pl: 1 }}
                >
                  {new Date(timestamp).toLocaleDateString("sv-SE")}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mx: 2, mb: 0.5 }}>
                {comment}
              </Typography>
            </Fragment>
          ))}
        </Box>
      </Paper>
    </Portal>
  );
};
